import { useContext } from 'react'

import { faTrashAlt } from '@fortawesome/pro-light-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

import { digObject } from '@domain-group/javascript-utils'

import {
  Box, Button, Form, Image, Link, ModalContext, SidebarModal,
} from '@domain-group/suit-theme'

import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'

import { confirmDelete } from '@functions/entity'
import useSavedArtworkPage, { useSavedArtworkPageForm } from '@hooks/useSavedArtworkPage'

const MODAL_KEY = 'ManageSavedArtworkPageModal'

type ModalCallbacks = {
  closeModal: () => void,
  deleteSavedArtworkPage: (payload: HandleCallbackActionParams) => void,
  updateSavedArtworkPage: (payload: HandleCallbackActionParams) => void,
}

type ModalProps = {
  callbacks: ModalCallbacks,
  showModal: boolean,
}

const ManageSavedArtworkPageModal = (props: ModalProps) => {
  const { callbacks, showModal } = props
  const { closeModal, updateSavedArtworkPage } = callbacks || {}

  const modalContext = useContext(ModalContext)
  const { modalData } = modalContext

  const { savedArtworkPage } = digObject(modalData, MODAL_KEY, {})

  const savedArtworkPageFormPayload = useSavedArtworkPageForm(savedArtworkPage, { validateOn: 'change' })
  const {
    entityState,
    entityState: {
      title,
    },
    errors,
    handlers,
    saveEnabled,
  } = savedArtworkPageFormPayload

  const { previews } = savedArtworkPage

  const {
    callbacks: { deleteSavedArtworkPage: deleteFn, updateSavedArtworkPage: updateFn },
    deleting,
    updating,
  } = useSavedArtworkPage(savedArtworkPage)

  const updateSavedArtworkPagePayload = {
    callbacks: {
      action: updateFn,
      afterAction: closeModal,
    },
    entityParams: entityState,
    toastText: 'Saved Artwork Page Updated',
  }

  const confirmDeletePayload = {
    callbacks: {
      afterAction: closeModal,
      deleteFn,
    },
    options: {
      entityParams: savedArtworkPage,
    },
    strings: {
      confirmString: 'Are you sure?',
      entityName: 'Saved Artwork Page',
    },
  }

  return (
    <SidebarModal callbacks={callbacks} clickSafeZone modalKey={MODAL_KEY} showModal={showModal}>
      <SidebarModal.Header callbacks={callbacks} title="Manage Saved" titleSecondLine="Artwork Page" />

      <SidebarModal.Content>
        <Form.Field direction="column" label="Preview">
          <Box variant="whiteGrey">
            <Link href={previews?.original} target="_blank" textProps={{ width: '100%', height: '100%' }}>
              <Image
                backgroundSize="contain"
                borderRadius={5}
                forwardProps={{ position: 'relative' }}
                height={240}
                // @ts-expect-error TS2322
                placeHolder
                url={previews?.thumb}
              />
            </Link>
          </Box>
        </Form.Field>

        <Form.Field label="Title" errorMessage={errors.title} marginTop="large">
          <input value={title} name="title" {...handlers} />
        </Form.Field>

        <Form.Field label="More Options" marginTop="large">
          <Button
            buttonStyle="secondaryUtility"
            icon={<FontAwesomeIcon icon={faTrashAlt} />}
            loading={deleting}
            onClick={() => confirmDelete(confirmDeletePayload)}
            size="medium"
          >
            Delete Saved Artwork
          </Button>
        </Form.Field>
      </SidebarModal.Content>

      <SidebarModal.Footer>
        <Button
          buttonStyle="primaryCreate"
          disabled={!saveEnabled}
          loading={updating}
          onClick={() => updateSavedArtworkPage(updateSavedArtworkPagePayload)}
          size="large"
        >
          {saveEnabled ? 'Save Saved Artwork Page' : 'Complete Fields'}
        </Button>
      </SidebarModal.Footer>
    </SidebarModal>
  )
}

const LazyLoadedModal = (props: ModalProps) => (
  <SidebarModal.RenderController {...props}>
    <ManageSavedArtworkPageModal {...props} />
  </SidebarModal.RenderController>
)

export default LazyLoadedModal
